import { CourierColors, CourierIconSVGs, SystemThemeMode, CourierFontTheme, CourierIconTheme } from "@trycourier/courier-ui-core";

// Re-export common types from core for convenience

/** @public */
export type CourierToastFontTheme = CourierFontTheme;

/** @public */
export type CourierToastIconTheme = CourierIconTheme & {
  /**
   * Whether the icon is drawn at all. Defaults to `true`; set it to `false` to
   * drop the icon and the space it holds, for toasts that read better as text
   * alone.
   */
  visible?: boolean;
};

/** @public */
export type CourierToastItemTheme = {
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  activeBackgroundColor?: string;
  autoDismissBarColor?: string;
  title?: CourierToastFontTheme;
  body?: CourierToastFontTheme;
  icon?: CourierToastIconTheme;
  dismissIcon?: CourierToastIconTheme;
  shadow?: string;
  border?: string;
  borderRadius?: string;
  actions?: {
    backgroundColor?: string;
    hoverBackgroundColor?: string;
    activeBackgroundColor?: string;
    border?: string;
    borderRadius?: string;
    shadow?: string;
    font?: CourierToastFontTheme;
  }
}

/** @public */
export type CourierToastTheme = {
  item?: CourierToastItemTheme;
};

/** @public */
export const defaultLightTheme: CourierToastTheme = {
  item: {
    backgroundColor: CourierColors.white[500],
    hoverBackgroundColor: CourierColors.gray[200],
    activeBackgroundColor: CourierColors.gray[500],
    shadow: `0px 4px 8px -2px ${CourierColors.black[500_20]}`,
    border: `1px solid ${CourierColors.gray[500]}`,
    borderRadius: '8px',
    title: {
      size: '11pt',
      weight: '400',
      color: CourierColors.black[500],
    },
    body: {
      size: '11pt',
      weight: '400',
      color: CourierColors.gray[600],
    },
    icon: {
      color: CourierColors.black[500],
      svg: CourierIconSVGs.inbox,
    },
    dismissIcon: {
      color: CourierColors.black[500],
      svg: CourierIconSVGs.remove,
    },
    autoDismissBarColor: CourierColors.blue[400],
    actions: {
      backgroundColor: 'transparent',
      hoverBackgroundColor: CourierColors.gray[200],
      activeBackgroundColor: CourierColors.gray[500],
      border: `1px solid ${CourierColors.gray[500]}`,
      borderRadius: '4px',
      shadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
      font: {
        color: CourierColors.black[500],
        size: '14px',
      }
    }
  }
};

/** @public */
export const defaultDarkTheme: CourierToastTheme = {
  item: {
    backgroundColor: CourierColors.black[500],
    hoverBackgroundColor: CourierColors.gray[800],
    activeBackgroundColor: CourierColors.gray[700],
    shadow: `0px 4px 8px -2px ${CourierColors.gray[400]}`,
    border: `1px solid ${CourierColors.gray[400]}`,
    borderRadius: '8px',
    title: {
      size: '11pt',
      weight: '400',
      color: CourierColors.white[500],
    },
    body: {
      size: '11pt',
      weight: '400',
      color: CourierColors.gray[500],
    },
    icon: {
      color: CourierColors.white[500],
      svg: CourierIconSVGs.inbox,
    },
    dismissIcon: {
      color: CourierColors.white[500],
      svg: CourierIconSVGs.remove,
    },
    autoDismissBarColor: CourierColors.blue[400],
    actions: {
      backgroundColor: CourierColors.black[400],
      hoverBackgroundColor: CourierColors.white[500_10],
      activeBackgroundColor: CourierColors.white[500_20],
      border: `1px solid ${CourierColors.gray[400]}`,
      borderRadius: '4px',
      shadow: `0px 1px 2px 0px ${CourierColors.white[500_10]}`,
      font: {
        color: CourierColors.white[500],
        size: '14px'
      }
    }
  }
};

/**
 * Deep merge themes, only overwriting non-optional properties.
 *
 * @public
 */
export const mergeTheme = (mode: SystemThemeMode, theme: CourierToastTheme): CourierToastTheme => {
  const defaultTheme = mode === 'light' ? defaultLightTheme : defaultDarkTheme;
  return {
    item: {
      ...defaultTheme.item,
      ...theme.item,
      title: {
        ...defaultTheme.item?.title,
        ...theme.item?.title
      },
      body: {
        ...defaultTheme.item?.body,
        ...theme.item?.body
      },
      icon: {
        ...defaultTheme.item?.icon,
        ...theme.item?.icon
      },
      dismissIcon: {
        ...defaultTheme.item?.dismissIcon,
        ...theme.item?.dismissIcon
      }
    }
  };
};
